import React, { FC } from 'react';
import { Link as RouterLink } from 'react-router';

import { dayjs } from '@tsl-frontend/tsl-utilities';

import {
    Box,
    Chip,
    Grid,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
    IconButton,
    Icons,
    Stack,
    TSLLink,
    Typography,
} from '@thirdspacelearning/library';

import { Country, LearningObjective } from '@types';
import { CMSDataGrid } from '@shared';

interface Props {
    learningObjectives: LearningObjective[];
    total: number;
    loading: boolean;
    paginationModel: GridPaginationModel;
    onPaginationModelChange: (paginationModel: GridPaginationModel) => void;
    onSortModelChange: (sortModel: GridSortModel) => void;
    onEditObjective: (lo: LearningObjective) => void;
    country: Country;
}

const LearningObjectivesTable: FC<Props> = ({
    learningObjectives,
    total,
    loading,
    paginationModel,
    onPaginationModelChange,
    onSortModelChange,
    onEditObjective,
    country,
}) => {
    const columns: GridColDef<LearningObjective>[] = [
        {
            field: 'id',
            type: 'string',
            headerName: 'ID',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            flex: 0,
            width: 70,
        },
        {
            field: 'title',
            type: 'string',
            headerName: 'TSL Learning Objective',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            renderCell: ({ row }) => (
                <Box sx={{ whiteSpace: 'normal' }}>
                    {row.ai && (
                        <Chip
                            label={'AI'}
                            color="info"
                            size="small"
                            sx={{ mb: '2px', mr: 1 }}
                            data-testid="ai-chip"
                        />
                    )}
                    {row.title}
                </Box>
            ),
            flex: 3,
            minWidth: 250,
        },
        {
            field: 'active',
            type: 'string',
            headerName: 'Active',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            renderCell: (params) =>
                params.row.active ? (
                    <Icons.Check
                        sx={{ color: 'success.main' }}
                        data-testid="active"
                    />
                ) : (
                    <Icons.Clear
                        sx={{ color: 'error.main' }}
                        data-testid="inactive"
                    />
                ),
            maxWidth: 100,
        },
        {
            field: 'year_id',
            type: 'string',
            headerName: country === Country.GB ? 'Year Group' : 'Grade',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            flex: 1,
            maxWidth: 150,
        },
        {
            field: 'standard_codes',
            type: 'string',
            headerName: 'Standard Code',
            headerAlign: 'left',
            align: 'left',
            renderCell: ({ row }) =>
                'standard_codes' in row && (
                    <Stack direction="row" flexWrap="wrap">
                        {row.standard_codes.map((standardCode, idx) => (
                            <Chip
                                key={idx}
                                label={standardCode}
                                variant="outlined"
                                size="small"
                                sx={{ mb: '2px', mr: 1 }}
                                data-testid="standard-code-chip"
                            />
                        ))}
                    </Stack>
                ),
            flex: 1,
        },
        {
            field: 'learning_content',
            headerName: 'Learning Content',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            renderCell: ({ row }) => (
                <Grid container alignItems="center">
                    <Grid item xs={7} data-testid="slides-count">
                        {`${row.slides_counter} Slides`}
                    </Grid>
                    <TSLLink
                        component={RouterLink}
                        to={`/learning-content/${row.id}`}
                        color="primary"
                        data-testid="slides-link"
                    >
                        {row.slides_counter ? 'Edit' : 'Add'}
                    </TSLLink>
                </Grid>
            ),
            maxWidth: 200,
            flex: 1,
        },
        {
            field: 'created_at',
            type: 'string',
            headerName: 'Created date',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            valueGetter: (value) =>
                value && dayjs(value).format('DD/MM/YY HH:mm'),
            renderCell: ({ row }) => (
                <Typography variant="caption" data-testid="created_at">
                    {row.created_at &&
                        dayjs(row.created_at).format('DD/MM/YY HH:mm')}
                </Typography>
            ),
            flex: 1,
            maxWidth: 200,
        },
        {
            field: 'updated_at',
            type: 'string',
            headerName: 'Last modified date',
            headerAlign: 'left',
            align: 'left',
            sortable: true,
            valueGetter: (value) =>
                value && dayjs(value).format('DD/MM/YY HH:mm'),
            renderCell: ({ row }) => (
                <Typography variant="caption" data-testid="updated_at">
                    {row.updated_at &&
                        dayjs(row.updated_at).format('DD/MM/YY HH:mm')}
                </Typography>
            ),
            flex: 1,
            maxWidth: 250,
        },
        {
            field: 'actions',
            type: 'string',
            display: 'flex',
            headerName: '',
            renderCell: ({ row }) => (
                <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => onEditObjective(row)}
                    data-testid="slides-edit-button"
                >
                    <Icons.Edit color="primary" />
                </IconButton>
            ),
            sortable: false,
            width: 52,
        },
    ];

    return (
        <CMSDataGrid
            columns={columns}
            rows={learningObjectives}
            rowCount={total}
            paginationMode="server"
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            columnVisibilityModel={{
                standard_codes: country === Country.US,
            }}
            filterMode="server"
            sortingMode="server"
            onSortModelChange={onSortModelChange}
            loading={loading}
            slotProps={{
                loadingOverlay: { variant: 'linear-progress' },
            }}
            localeText={{
                noRowsLabel:
                    "We're sorry, we couldn't find any learning objectives with those filters",
            }}
            disableColumnMenu
            disableColumnSelector
            disableColumnFilter
            disableDensitySelector
            disableRowSelectionOnClick
            disableColumnResize
        />
    );
};

export default LearningObjectivesTable;
